import { useTranslation } from "react-i18next";
import type { OnboardingStepKey } from "@/types/onboarding";
import { cn } from "@/lib/utils";

interface OnboardingDevStepRailProps {
  steps: OnboardingStepKey[];
  activeStep: OnboardingStepKey;
  onJump: (key: OnboardingStepKey) => void;
}

/** Fixed strip of step jumps; only when `import.meta.env.DEV` (omit from staging builds that use a non-dev Vite mode). */
export function OnboardingDevStepRail({
  steps,
  activeStep,
  onJump,
}: OnboardingDevStepRailProps) {
  const { t } = useTranslation("onboarding");
  if (!import.meta.env.DEV) return null;

  return (
    <div
      className="pointer-events-auto fixed bottom-0 left-0 right-0 z-[100] border-t border-amber-500/40 bg-zinc-950/95 px-2 py-2 text-zinc-100 shadow-lg backdrop-blur-sm"
      data-testid="onboarding-dev-step-rail"
    >
      <div className="mx-auto flex max-w-4xl flex-col gap-1.5">
        <div className="flex items-center justify-between gap-2 px-0.5">
          <span className="font-mono text-[10px] font-semibold uppercase tracking-wide text-amber-400/90">
            Dev · onboarding steps
          </span>
          <span className="font-mono text-[10px] text-zinc-500">
            Saves/API kräver riktig inloggning
          </span>
        </div>
        <div className="flex flex-wrap gap-1">
          {steps.map((key) => (
            <button
              key={key}
              type="button"
              onClick={() => onJump(key)}
              title={t(`stepTitles.${key.replace(/-([a-z])/g, (_, c) => c.toUpperCase())}`, { defaultValue: key })}
              className={cn(
                "rounded border px-1.5 py-1 font-mono text-[10px] leading-none transition-colors",
                key === activeStep
                  ? "border-amber-400 bg-amber-500/20 text-amber-100"
                  : "border-zinc-600 bg-zinc-900 text-zinc-300 hover:border-zinc-500 hover:bg-zinc-800",
              )}
            >
              {key}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
